// @admin-patient-requests-v1 (20 Oct 2026)
// «Αναρρωτικές Άδειες» and «Επαναλήψεις Συνταγών» tabs on the admin patient profile.
// One row per paid request, newest first. «Προβολή» opens the consultation page,
// which shows the doctor's case screen for that request (@admin-async-case-v1).
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './Shared';
import { asyncCaseAPI } from '../services/api';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('el-GR') : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('el-GR', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

const STATUS = {
  open: 'Στην ουρά', claimed: 'Σε αξιολόγηση', info_requested: 'Αναμονή απάντησης ασθενή',
  completed: 'Ολοκληρώθηκε', expired: 'Έληξε', closed_no_reply: 'Έκλεισε χωρίς απάντηση',
};
const SL_DECISION = { issued: 'Εκδόθηκε', declined: 'Άρνηση', referred: 'Παραπομπή' };

const TONES = {
  green: ['#E7F7EF', '#0D7A52'], red: ['#FDECEC', '#B42318'], amber: ['#FFF4E5', '#B45309'], grey: ['#F1F3F6', '#4A5568'],
};
const Pill = ({ tone, children }) => (
  <span style={{ background: TONES[tone][0], color: TONES[tone][1], borderRadius: 999, padding: '3px 10px',
    fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{children}</span>
);

const sickLeaveResult = (r) => {
  if (r.status !== 'completed') return [STATUS[r.status] || r.status, 'grey'];
  return [SL_DECISION[r.decision] || 'Ολοκληρώθηκε', r.decision === 'issued' ? 'green' : 'red'];
};
const renewalResult = (r) => {
  if (r.status !== 'completed') return [STATUS[r.status] || r.status, 'grey'];
  if (!r.approved_count) return ['Απορρίφθηκε', 'red'];
  if (r.approved_count < r.item_count) return ['Μερική έγκριση', 'amber'];
  return ['Εγκρίθηκε', 'green'];
};

const th = { textAlign: 'left', padding: 12, color: 'var(--muted)', fontWeight: 600 };
const td = { padding: 12, borderTop: '1px solid var(--border)', verticalAlign: 'middle' };

export default function PatientAsyncRequestsTab({ patientId, kind }) {
  const navigate = useNavigate();
  const isSick = kind === 'sick_leave';
  const title = isSick ? '📝 Αναρρωτικές Άδειες' : '💊 Επαναλήψεις Συνταγών';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = isSick
        ? await asyncCaseAPI.sickLeaveForPatient(patientId)
        : await asyncCaseAPI.renewalsForPatient(patientId);
      setRows(res.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Αποτυχία φόρτωσης');
    } finally {
      setLoading(false);
    }
  }, [patientId, isSick]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Card title={title}><p style={{ color: '#6B7280', padding: 16 }}>Φόρτωση…</p></Card>;
  if (error) return <Card title={title}><p style={{ color: '#DC2626', padding: 16 }}>{error}</p></Card>;
  if (rows.length === 0) {
    return (
      <Card title={title}>
        <p style={{ color: '#6B7280', padding: 16 }}>
          {isSick ? 'Ο ασθενής δεν έχει αιτήματα αναρρωτικής άδειας.' : 'Ο ασθενής δεν έχει αιτήματα επανάληψης συνταγής.'}
        </p>
      </Card>
    );
  }

  return (
    <Card title={`${title} (${rows.length})`}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={th}>Υποβολή</th>
              <th style={th}>Ιατρός</th>
              <th style={th}>{isSick ? 'Ημέρες' : 'Φάρμακα'}</th>
              <th style={th}>Αποτέλεσμα</th>
              <th style={th}>Ολοκλήρωση</th>
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const [label, tone] = isSick ? sickLeaveResult(r) : renewalResult(r);
              const detail = isSick
                ? (r.decision === 'issued' && r.issued_days
                  ? `${r.issued_days} (${fmtDate(r.issued_start)} – ${fmtDate(r.issued_end)})`
                  : `ζήτησε ${r.requested_days || '—'} από ${fmtDate(r.absence_start)}`)
                : `${r.approved_count || 0}/${r.item_count || 0} εγκρίθηκαν`;
              return (
                <tr key={r.id}>
                  <td style={td}>{fmtDateTime(r.paid_at || r.created_at)}</td>
                  <td style={td}>{r.doctor_name ? `Δρ. ${r.doctor_name}` : '—'}</td>
                  <td style={td}>{detail}</td>
                  <td style={td}>
                    <Pill tone={tone}>{label}</Pill>
                    {(r.refund_status === 'refunded' || r.refund_status === 'pending') && (
                      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                        {r.refund_status === 'refunded' ? 'Επιστροφή χρημάτων' : 'Επιστροφή σε εξέλιξη'}
                      </div>
                    )}
                  </td>
                  <td style={td}>{fmtDateTime(r.completed_at)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button
                      type="button"
                      disabled={!r.appointment_id}
                      onClick={() => navigate(`/consultations/${r.appointment_id}`)}
                      style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 14px',
                        fontWeight: 600, fontSize: 13, cursor: r.appointment_id ? 'pointer' : 'default',
                        opacity: r.appointment_id ? 1 : 0.5 }}
                    >
                      Προβολή
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
